import type { CSSProperties, ReactNode } from 'react'

/** Lattice unit shared by pipes + placement. Keep glyphs on this grid. */
export const U = 24

export interface PidDiagramProps {
  cols: number
  rows: number
  children?: ReactNode
  className?: string
  style?: CSSProperties
  /** Debug: draw the 24px grid. */
  showGrid?: boolean
}

/**
 * Absolute canvas on a 24px lattice. Pipe runs and equipment ports share
 * lattice points so strokes meet without flex/gap math.
 */
export function PidDiagram({ cols, rows, children, className, style, showGrid }: PidDiagramProps) {
  return (
    <div
      className={['pid-diagram', className].filter(Boolean).join(' ')}
      style={{
        position: 'relative',
        width: cols * U,
        height: rows * U,
        ...style
      }}
    >
      {showGrid && <div className="pid-diagram-grid" aria-hidden />}
      {children}
    </div>
  )
}

export interface AtProps {
  col: number
  row: number
  children?: ReactNode
  className?: string
  style?: CSSProperties
  z?: number
}

/**
 * Places children so the host's top-left is lattice point (col, row).
 *
 * - Pipes: use as-is — Pipe SVGs offset themselves so the stroke centerline
 *   passes through that point.
 * - Equipment: wrap in PidNode — it centers the glyph on the point and parks
 *   readouts/tags below without shifting the port.
 */
export function At({ col, row, children, className, style, z = 1 }: AtProps) {
  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        left: col * U,
        top: row * U,
        zIndex: z,
        ...style
      }}
    >
      {children}
    </div>
  )
}
